import camelcaseKeys from "camelcase-keys";
import { AxiosError, AxiosResponse } from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AXIOS } from "../axios";

import { useAuth } from "../../components/auth/AuthProvider";
import { useError } from "../../components/status/ErrorProvider";
import { useSuccess } from "../../components/status/SuccessProvider";
import { MetaSchema, PaginationSchema } from "../pagination";

export type KeyType = "wrapping" | "wrapped" | "composite" | "component" | "p12";

export type KeySchema = {
  id: string;
  name: string;
  size: number;
  type: KeyType;
  tags: string[];
  email: string;
  useId: string;
  buffer: string;
  project: {
    id: string;
    name: string;
  };
  status:
    | "uploaded"
    | "pending"
    | "unverified"
    | "issued"
    | "imported"
    | "pushed"
    | "scheduled"
    | "incomplete"
    | "expired"
    | "archived"
    | "rejected";
  createdAt: string;
  algorithm: OutputKeyAlgo;
  requestId: string;
  partNumber: string;
  description: string;
  expectedKcv: string;
  computedKcv: string;
  publicHandle: string;
  privateHandle: string;
  correlationId: string;
  importedHandle: string;
  wrappingAlgo: KeyWrapAlgorithm;
  wrappingKeyId: string;
  originalFileName: string;
  compositeId: string;
  compositeName: string;
  compositeTotal: string;
  compositeTotalImported: string;
  compositeAlgorithm: OutputKeyAlgo;
  compositePartNumber: string;
  compositeExpectedKcv: string;
  assetId: string;
  assetPackage: string;
};

export type KeyGenAlgorithm =
  | "RSA2048"
  | "RSA3072"
  | "RSA4096"
  | "EC256"
  | "EC384"
  | "EC521"
  | "DH1024"
  | "DH2048"
  | "DH4096"
  | "MLDSA44"
  | "MLDSA65"
  | "MLDSA87"
  | "MLKEM512"
  | "MLKEM768"
  | "MLKEM1024";

export type KeyRequestSchema = {
  projectId: string;
  algorithm: KeyGenAlgorithm | "";
  description?: string;
};

type KeyDownloadSchema = {
  keyId: string;
};

export type KeyWrapAlgorithm =
  | "RSA_OEAP"
  | "RSA_OAEP_SHA1"
  | "RSA_OAEP_SHA256"
  | "RSA_OAEP_SHA512"
  | "RSA_PKCS"
  | "MLKEM"
  | "UNWRAPKEY_AES_CBC"
  | "UNWRAPKEY_AES_CBC_PAD"
  | "";

export type OutputKeyAlgo = "AES128" | "AES192" | "AES256" | "AES128_CMAC" | "AES256_CMAC" | "";

type KeyUploadWrappedSchema = {
  file: File | undefined;
  projectId: string;
  wrappingAlgo: KeyWrapAlgorithm;
  wrappingKeyId: string;
  outputKeyName: string;
  outputKeyAlgo: OutputKeyAlgo;
  partNumber?: string;
  description?: string;
  expectedKcv?: string;
};

type KeyUploadComponentSchema = {
  file: File | undefined;
  projectId: string;
  compositeName: string;
  compositeAlgo: OutputKeyAlgo;
  compositeType: "new" | "existing";
  compositeTotal: string;
  partNumber?: string;
  description?: string;
  expectedKcv?: string;
};

type KeyUploadP12Schema = {
  file: File | undefined;
  projectId: string;
  outputKeyName: string;
  partNumber?: string;
  description?: string;
};

type KeyImportP12Schema = {
  keyId: string;
  password?: string;
};

type KeyDescriptionSchema = {
  id: string;
  description: string;
};

type KeyPartNumberSchema = {
  id: string;
  partNumber: string;
};

type KeyTagsSchema = {
  id: string;
  tags: string[];
};

type useProxyKeysSchema = PaginationSchema & {
  type?: string;
  email?: string;
  status?: string;
  projectId?: string;
  projectName?: string;
  showArchived?: boolean;
};

export const useProxyKeys = (body?: useProxyKeysSchema) => {
  const url = useAuth().user.activeOrganization.url;

  return useQuery<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    { data: KeySchema[]; meta: MetaSchema }
  >({
    queryKey: [`PROXY KEYS ${JSON.stringify(body)}`],
    queryFn: () => AXIOS.post(`${url}/keys`, body),
    select: (data) => camelcaseKeys(data.data, { deep: true })
  });
};

export const useProxyKeyComposites = (body?: useProxyKeysSchema) => {
  const url = useAuth().user.activeOrganization.url;

  return useQuery<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    { data: KeySchema[]; meta: MetaSchema }
  >({
    queryKey: [`PROXY COMPOSITES ${JSON.stringify(body)}`],
    queryFn: () => AXIOS.post(`${url}/keys/composites`, body),
    select: (data) => camelcaseKeys(data.data, { deep: true })
  });
};

export const useProxyKeyRequest = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, KeyRequestSchema>({
    mutationKey: [`PROXY KEYS REQUEST`],
    mutationFn: (props: KeyRequestSchema) => AXIOS.post(`${url}/keys/request`, props),
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: (data) => {
      setSuccess(`Key requested`);

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY KEYS")
      });
    }
  });

  return mutation;
};

export const useProxyKeyDownload = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, KeyDownloadSchema>({
    mutationKey: [`PROXY KEYS DOWNLOAD`],
    mutationFn: (props: KeyDownloadSchema) =>
      AXIOS.post(`${url}/keys/download`, props, {
        responseType: "arraybuffer"
      }),
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";

      setError({ message, reset: mutation.reset });
    },
    onSuccess: ({ data, headers }) => {
      const blob = new Blob([data], { type: "application/octet-stream" });

      const filename = headers["content-filename"] || `${new Date().toISOString()}.bin`;

      // create & download file
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      // clean up
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(link.href);

      setSuccess(`Downloaded: ${filename}`);

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY KEYS")
      });
    }
  });

  return mutation;
};

export const useProxyKeyUploadWrapped = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    KeyUploadWrappedSchema
  >({
    mutationKey: [`PROXY KEYS UPLOAD WRAPPED`],
    mutationFn: (props: KeyUploadWrappedSchema) =>
      AXIOS.post(`${url}/keys/upload/wrapped`, props, {
        headers: { "Content-Type": "multipart/form-data" }
      }),
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: (data) => {
      setSuccess(`Key uploaded`);

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY KEYS")
      });
    }
  });

  return mutation;
};

export const useProxyKeyUploadComponent = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    KeyUploadComponentSchema
  >({
    mutationKey: [`PROXY KEYS UPLOAD COMPONENT`],
    mutationFn: (props: KeyUploadComponentSchema) =>
      AXIOS.post(`${url}/keys/upload/component`, props, {
        headers: { "Content-Type": "multipart/form-data" }
      }),
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: (data) => {
      setSuccess(`Key uploaded`);

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY KEYS")
      });
    }
  });

  return mutation;
};

export const useProxyKeyUploadP12 = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, KeyUploadP12Schema>({
    mutationKey: [`PROXY KEYS UPLOAD P12`],
    mutationFn: (props: KeyUploadP12Schema) =>
      AXIOS.post(`${url}/keys/upload/p12`, props, {
        headers: { "Content-Type": "multipart/form-data" }
      }),
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: (data) => {
      setSuccess(`P12 file uploaded`);

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY KEYS")
      });
    }
  });

  return mutation;
};

export const useProxyKeyRemove = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, { keyId: string }>({
    mutationKey: [`PROXY KEYS REMOVE`],
    mutationFn: (props: { keyId: string }) => AXIOS.post(`${url}/keys/remove`, props),
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: (data) => {
      setSuccess(`Key removed`);

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY KEYS")
      });
    }
  });

  return mutation;
};

export const useProxyKeyImportWrapped = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, { keyId: string }>({
    mutationKey: [`PROXY KEYS IMPORT WRAPPED`],
    mutationFn: (props: { keyId: string }) => AXIOS.post(`${url}/keys/import/wrapped`, props),
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: (data) => {
      if (data.status === 200) {
        setSuccess(`Key imported`);
      }

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY KEYS")
      });
    }
  });

  return mutation;
};

export const useProxyKeyImportComponent = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, { keyId: string }>({
    mutationKey: [`PROXY KEYS IMPORT COMPONENT`],
    mutationFn: (props: { keyId: string }) => AXIOS.post(`${url}/keys/import/component`, props),
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: (data) => {
      if (data.status === 200) {
        setSuccess(`Key imported`);
      }

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY KEYS")
      });
    }
  });

  return mutation;
};

export const useProxyKeyImportP12 = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, KeyImportP12Schema>({
    mutationKey: [`PROXY KEYS IMPORT P12`],
    mutationFn: (props: KeyImportP12Schema) => AXIOS.post(`${url}/keys/import/p12`, props),
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: (data) => {
      if (data.status === 200) {
        setSuccess(`P12 file imported`);
      }

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY KEYS")
      });
    }
  });

  return mutation;
};

export const useProxyKeyVerify = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, { keyId: string }>({
    mutationKey: [`PROXY KEYS VERIFY`],
    mutationFn: (props: { keyId: string }) => AXIOS.post(`${url}/keys/verify`, props),
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: (data) => {
      setSuccess(`Key verified`);

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY KEYS")
      });
    }
  });

  return mutation;
};

export const useProxyKeyPush = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, { keyId: string }>({
    mutationKey: [`PROXY KEYS PUSH`],
    mutationFn: (props: { keyId: string }) => AXIOS.post(`${url}/keys/push`, props),
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: (data) => {
      setSuccess(`Key pushed`);

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY KEYS")
      });
    }
  });

  return mutation;
};

export const useProxyKeyPushSchedule = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, { keyId: string }>({
    mutationKey: [`PROXY KEYS PUSH SCHEDULE`],
    mutationFn: (props: { keyId: string }) => AXIOS.post(`${url}/keys/push/schedule`, props),
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: (data) => {
      setSuccess(`Key push scheduled`);

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY KEYS")
      });
    }
  });

  return mutation;
};

export const useProxyKeyPushCancel = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, { keyId: string }>({
    mutationKey: [`PROXY KEYS PUSH CANCEL`],
    mutationFn: (props: { keyId: string }) => AXIOS.post(`${url}/keys/push/cancel`, props),
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: (data) => {
      setSuccess(`Key push canceled`);

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY KEYS")
      });
    }
  });

  return mutation;
};

export const useProxyKeyEditTags = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<KeySchema, any>, AxiosError<{ error: string; message: string }>, KeyTagsSchema>(
    {
      mutationKey: [`PROXY KEYS EDIT TAGS`],
      mutationFn: (props: KeyTagsSchema) => AXIOS.post(`${url}/keys/edit/tags`, props),
      onError: (error) => {
        const message = error.response?.data.message ?? "please try again";
        setError({ message, reset: mutation.reset });
      },
      onSuccess: (data) => {
        setSuccess(`Tags updated`);

        queryClient.invalidateQueries({
          predicate: (query) =>
            // @ts-ignore
            query.queryKey[0]?.startsWith("PROXY KEYS")
        });
      }
    }
  );

  return mutation;
};

export const useProxyKeyEditPartNumber = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<AxiosResponse<any, any>, AxiosError<{ error: string; message: string }>, KeyPartNumberSchema>(
    {
      mutationKey: [`PROXY KEYS EDIT PART NUMBER`],
      mutationFn: (props: KeyPartNumberSchema) => AXIOS.post(`${url}/keys/edit/partnumber`, props),
      onError: (error) => {
        const message = error.response?.data.message ?? "please try again";
        setError({ message, reset: mutation.reset });
      },
      onSuccess: (data) => {
        setSuccess(`Part number updated`);

        queryClient.invalidateQueries({
          predicate: (query) =>
            // @ts-ignore
            query.queryKey[0]?.startsWith("PROXY KEYS")
        });
      }
    }
  );

  return mutation;
};

export const useProxyKeyEditDescription = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    KeyDescriptionSchema
  >({
    mutationKey: [`PROXY KEYS EDIT DESCRIPTION`],
    mutationFn: (props: KeyDescriptionSchema) => AXIOS.post(`${url}/keys/edit/description`, props),
    onError: (error) => {
      const message = error.response?.data.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: (data) => {
      setSuccess(`Description updated`);

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY KEYS")
      });
    }
  });

  return mutation;
};
